import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiService from "../services/api";
import Button from "../components/ui/Button";
import { CheckCircle, XCircle, Loader } from "lucide-react";

export default function VerifyEmail() {
  const [status, setStatus] = useState("verifying"); // 'verifying' | 'success' | 'error'
  const [message, setMessage] = useState("");
  const { token } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus("error");
        setMessage("Verification token is missing");
        return;
      }

      try {
        const response = await apiService.verifyEmail(token);
        setStatus("success");
        setMessage(response.message || "Email verified successfully!");
      } catch (error) {
        console.error("Email verification error:", error);
        setStatus("error");
        setMessage(
          error.message || "Failed to verify email. The link may have expired.",
        );
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Email Verification
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            {status === "verifying" && (
              <div className="flex flex-col items-center">
                <Loader className="h-16 w-16 text-blue-600 animate-spin mb-4" />
                <p className="text-lg text-gray-700">
                  Verifying your email address...
                </p>
              </div>
            )}

            {status === "success" && (
              <div className="flex flex-col items-center">
                <CheckCircle className="h-16 w-16 text-green-600 mb-4" />
                <p className="text-lg text-gray-900 font-semibold mb-2">
                  Email Verified!
                </p>
                <p className="text-sm text-gray-600 mb-6">{message}</p>
                <Button
                  variant="primary"
                  onClick={() => navigate("/customer/login")}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Continue to Login
                </Button>
              </div>
            )}

            {status === "error" && (
              <div className="flex flex-col items-center">
                <XCircle className="h-16 w-16 text-red-600 mb-4" />
                <p className="text-lg text-gray-900 font-semibold mb-2">
                  Verification Failed
                </p>
                <p className="text-sm text-gray-600 mb-6">{message}</p>
                <div className="space-y-3 w-full">
                  <Button
                    variant="primary"
                    onClick={() => navigate("/customer/login")}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Back to Login
                  </Button>
                  <p className="text-sm text-center text-gray-600">
                    Need a new link?{" "}
                    <button
                      type="button"
                      onClick={() => navigate("/resend-verification")}
                      className="font-medium text-blue-600 hover:text-blue-500"
                    >
                      Resend verification email
                    </button>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
